import React, { useEffect, useState } from "react";
import { Handshake, Plus, Copy, Power, Banknote, ChevronDown, ChevronUp, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const inputCls =
  "bg-obsidian-900 border border-obsidian-600 px-2.5 py-2 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze text-sm";
const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

// Admin-only affiliate program panel: approve affiliates, copy their referral
// link, watch 1% commissions accrue, and record cash payouts.
export const AffiliatesPanel = () => {
  const [affs, setAffs] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [openLedger, setOpenLedger] = useState(null); // affiliate id
  const [ledger, setLedger] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/affiliates");
      setAffs(r.data || []);
    } catch {}
  };
  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return toast.error("Name and email required");
    setBusy(true);
    try {
      await api.post("/affiliates", { name, email });
      toast.success("Affiliate approved — share their link");
      setName("");
      setEmail("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create affiliate");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = (a) => {
    const link = `${window.location.origin}/r/${a.code}`;
    navigator.clipboard?.writeText(link);
    toast.success(`Copied ${link}`);
  };

  const toggleActive = async (a) => {
    try {
      await api.put(`/affiliates/${a.id}`, { active: !a.active });
      toast.success(a.active ? `${a.name} deactivated` : `${a.name} reactivated`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const recordPayout = async (a) => {
    const val = window.prompt(
      `Record a cash payout for ${a.name}.\nBalance owed: ${money(a.balance_cents)}\n\nAmount in dollars:`,
      (a.balance_cents / 100).toFixed(2),
    );
    if (val === null) return;
    const cents = Math.round(parseFloat(val) * 100);
    if (!cents || cents <= 0) return toast.error("Enter a valid amount");
    try {
      const r = await api.post(`/affiliates/${a.id}/payouts`, { amount_cents: cents, note: "Cash payout" });
      toast.success(`Payout recorded — new balance ${money(r.data.balance_cents)}`);
      load();
      if (openLedger === a.id) viewLedger(a);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payout failed");
    }
  };

  const [digestBusy, setDigestBusy] = useState(false);
  const sendDigest = async () => {
    setDigestBusy(true);
    try {
      const r = await api.post("/affiliates/digest/send");
      if (!r.data.sent && r.data.detail) toast.info(r.data.detail);
      else if (r.data.sent) toast.success(`Digest emailed to ${r.data.to} — ${money(r.data.total_owed_cents)} owed across ${r.data.affiliate_count} affiliate${r.data.affiliate_count === 1 ? "" : "s"}`);
      else toast.error("Email could not be sent — check email settings");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send digest");
    } finally {
      setDigestBusy(false);
    }
  };

  const viewLedger = async (a) => {
    if (openLedger === a.id) {
      setOpenLedger(null);
      setLedger(null);
      return;
    }
    try {
      const r = await api.get(`/affiliates/${a.id}/ledger`);
      setLedger(r.data);
      setOpenLedger(a.id);
    } catch {
      toast.error("Could not load ledger");
    }
  };

  return (
    <div className="obs-card" data-testid="affiliates-panel">
      <div className="flex items-center gap-3 mb-1">
        <Handshake className="h-5 w-5 text-blaze" />
        <div>
          <div className="overline">/ Affiliate program</div>
          <h3 className="font-heading text-2xl uppercase">Referral partners</h3>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
        <p className="text-xs text-zinc-500 flex-1">
          Approved affiliates earn 1% of every purchase their referrals make. Payouts are cash —
          settle outside the app, then record them here. A "commissions owed" email lands in your
          inbox on the 1st of every month.
        </p>
        <button
          type="button"
          data-testid="affiliate-digest-send-btn"
          onClick={sendDigest}
          disabled={digestBusy}
          className="shrink-0 inline-flex items-center gap-2 border border-obsidian-600 px-3 py-2 rounded-sm text-xs uppercase tracking-wider text-zinc-300 hover:border-blaze hover:text-blaze disabled:opacity-50"
          title="Email me the commissions-owed summary now"
        >
          <Mail className="h-3.5 w-3.5" /> {digestBusy ? "Sending…" : "Email me summary"}
        </button>
      </div>

      <form onSubmit={create} className="flex flex-col sm:flex-row gap-2 mb-5">
        <input data-testid="affiliate-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Affiliate name" className={`${inputCls} flex-1`} />
        <input data-testid="affiliate-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@example.com" className={`${inputCls} flex-1`} />
        <button type="submit" data-testid="affiliate-create-btn" disabled={busy} className="blaze-btn px-5 py-2 text-xs inline-flex items-center justify-center gap-2 disabled:opacity-50">
          <Plus className="h-3.5 w-3.5" /> Approve affiliate
        </button>
      </form>

      {affs.length === 0 ? (
        <p className="text-sm text-zinc-500" data-testid="affiliates-empty">No affiliates yet.</p>
      ) : (
        <div className="space-y-2">
          {affs.map((a) => (
            <div key={a.id} className={`border rounded-sm ${a.active ? "border-obsidian-600" : "border-obsidian-700 opacity-60"}`} data-testid={`affiliate-row-${a.id}`}>
              <div className="flex items-center gap-3 flex-wrap p-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-zinc-100 font-medium">
                    {a.name}
                    {!a.active && <span className="ml-2 text-[9px] uppercase tracking-widest text-zinc-500 border border-obsidian-600 px-1.5 py-0.5 rounded-sm">Inactive</span>}
                  </div>
                  <div className="text-xs text-zinc-500 truncate">{a.email} · code <span className="font-mono text-zinc-300">{a.code}</span> · {(a.rate_bps / 100).toFixed(2)}%</div>
                </div>
                <div className="flex gap-4 text-center">
                  <div><div className="font-data text-sm">{a.referred_count}</div><div className="text-[9px] uppercase tracking-widest text-zinc-500">Referred</div></div>
                  <div><div className="font-data text-sm">{money(a.earned_cents)}</div><div className="text-[9px] uppercase tracking-widest text-zinc-500">Earned</div></div>
                  <div><div className="font-data text-sm text-blaze" data-testid={`affiliate-balance-${a.id}`}>{money(a.balance_cents)}</div><div className="text-[9px] uppercase tracking-widest text-zinc-500">Owed</div></div>
                </div>
                <div className="flex gap-1">
                  <button type="button" data-testid={`affiliate-copy-${a.id}`} onClick={() => copyLink(a)} className="p-2 text-zinc-500 hover:text-blaze" title="Copy referral link">
                    <Copy className="h-4 w-4" />
                  </button>
                  <button type="button" data-testid={`affiliate-payout-${a.id}`} onClick={() => recordPayout(a)} className="p-2 text-zinc-500 hover:text-blaze" title="Record cash payout">
                    <Banknote className="h-4 w-4" />
                  </button>
                  <button type="button" data-testid={`affiliate-toggle-${a.id}`} onClick={() => toggleActive(a)} className="p-2 text-zinc-500 hover:text-blaze" title={a.active ? "Deactivate" : "Reactivate"}>
                    <Power className="h-4 w-4" />
                  </button>
                  <button type="button" data-testid={`affiliate-ledger-${a.id}`} onClick={() => viewLedger(a)} className="p-2 text-zinc-500 hover:text-blaze" title="View ledger">
                    {openLedger === a.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {openLedger === a.id && ledger && (
                <div className="border-t border-obsidian-600 p-3 text-xs" data-testid={`affiliate-ledger-detail-${a.id}`}>
                  <div className="overline mb-2">Commissions ({ledger.commissions.length})</div>
                  {ledger.commissions.length === 0 && <p className="text-zinc-500 mb-2">No commissions yet.</p>}
                  {ledger.commissions.slice(0, 20).map((c) => (
                    <div key={c.id} className="flex justify-between gap-2 py-1 border-b border-obsidian-700/50">
                      <span className="text-zinc-400 truncate">{new Date(c.created_at).toLocaleDateString()} · {c.user_email || c.user_id} · {c.plan || "plan"}</span>
                      <span className="text-zinc-200 whitespace-nowrap">{money(c.commission_cents)} <span className="text-zinc-500">of {money(c.payment_amount_cents)}</span></span>
                    </div>
                  ))}
                  <div className="overline mt-3 mb-2">Payouts ({ledger.payouts.length})</div>
                  {ledger.payouts.length === 0 && <p className="text-zinc-500">No payouts recorded.</p>}
                  {ledger.payouts.map((p) => (
                    <div key={p.id} className="flex justify-between gap-2 py-1 border-b border-obsidian-700/50">
                      <span className="text-zinc-400">{new Date(p.created_at).toLocaleDateString()} · {p.note || "Payout"}</span>
                      <span className="text-zinc-200">-{money(p.amount_cents)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AffiliatesPanel;
